(() => {
  const gallerySelector = 'product-info media-gallery';
  const deferredVideoSelector = '.product-media-container.media-type-video > deferred-media';
  const initializedGalleries = new WeakSet();
  const preparedVideos = new WeakMap();

  const nextPaint = () =>
    new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

  const waitForCurrentData = (video) =>
    new Promise((resolve, reject) => {
      if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        resolve();
        return;
      }

      video.addEventListener('loadeddata', resolve, { once: true });
      video.addEventListener('error', reject, { once: true });
    });

  const prepareVideo = (deferredMedia) => {
    if (preparedVideos.has(deferredMedia)) return preparedVideos.get(deferredMedia);

    const promise = (async () => {
      const templateVideo = deferredMedia.querySelector('template')?.content.querySelector('video');
      if (!templateVideo) return null;

      deferredMedia.querySelector(':scope > video')?.remove();

      const video = templateVideo.cloneNode(true);
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.autoplay = true;
      video.loop = true;
      video.preload = 'auto';
      video.poster = '';
      video.setAttribute('muted', '');
      video.setAttribute('autoplay', '');
      video.setAttribute('loop', '');
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.removeAttribute('poster');
      video.style.setProperty('transform', 'translateZ(0)');
      video.style.setProperty('-webkit-transform', 'translateZ(0)');
      video.style.setProperty('will-change', 'transform');

      deferredMedia.appendChild(video);
      deferredMedia.setAttribute('loaded', 'true');

      // Force Safari to lay out a fresh video element before playback starts.
      void deferredMedia.offsetHeight;
      video.load();
      await waitForCurrentData(video);
      await nextPaint();

      if (video.currentTime === 0 && video.duration > 0.01) video.currentTime = 0.01;
      return video;
    })().catch((error) => {
      preparedVideos.delete(deferredMedia);
      console.warn('Unable to prepare inline product video', error);
      return null;
    });

    preparedVideos.set(deferredMedia, promise);
    return promise;
  };

  const visibleRatio = (element, viewer) => {
    const rect = element.getBoundingClientRect();
    if (!rect.width || !rect.height) return 0;

    const viewerRect = viewer.getBoundingClientRect();
    const clipTop = Math.max(0, viewerRect.top);
    const clipRight = Math.min(window.innerWidth, viewerRect.right);
    const clipBottom = Math.min(window.innerHeight, viewerRect.bottom);
    const clipLeft = Math.max(0, viewerRect.left);
    const visibleWidth = Math.max(0, Math.min(rect.right, clipRight) - Math.max(rect.left, clipLeft));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, clipBottom) - Math.max(rect.top, clipTop));

    return (visibleWidth * visibleHeight) / (rect.width * rect.height);
  };

  const initializeGallery = (gallery) => {
    if (initializedGalleries.has(gallery)) return;

    const deferredMediaElements = Array.from(gallery.querySelectorAll(deferredVideoSelector)).filter((element) =>
      element.querySelector('template')?.content.querySelector('video')
    );
    if (!deferredMediaElements.length) return;

    initializedGalleries.add(gallery);

    const viewer = gallery.querySelector('[id^="GalleryViewer"]') || gallery;
    const mediaItems = deferredMediaElements.map((element) => element.closest('.product__media-item')).filter(Boolean);
    let activeDeferredMedia = null;
    let syncQueued = false;

    const pause = async (deferredMedia) => {
      const video = await preparedVideos.get(deferredMedia);
      video?.pause();
    };

    const play = async (deferredMedia) => {
      const video = await prepareVideo(deferredMedia);
      if (!video || activeDeferredMedia !== deferredMedia) return;

      try {
        await video.play();
      } catch (error) {
        // Autoplay can still be denied by browser or user-level media preferences.
        console.warn('Inline product video autoplay was prevented', error);
      }
    };

    const syncPlayback = () => {
      syncQueued = false;

      const candidates = deferredMediaElements
        .map((deferredMedia) => ({
          deferredMedia,
          item: deferredMedia.closest('.product__media-item'),
        }))
        .filter(({ item }) => item)
        .map((candidate) => ({
          ...candidate,
          ratio: visibleRatio(candidate.item, viewer),
        }))
        .sort((a, b) => b.ratio - a.ratio);

      const nextDeferredMedia = candidates[0]?.ratio >= 0.5 ? candidates[0].deferredMedia : null;
      if (nextDeferredMedia === activeDeferredMedia) return;

      const previousDeferredMedia = activeDeferredMedia;
      activeDeferredMedia = nextDeferredMedia;

      if (previousDeferredMedia) pause(previousDeferredMedia);
      deferredMediaElements.forEach((deferredMedia) => {
        if (deferredMedia !== activeDeferredMedia && deferredMedia !== previousDeferredMedia) pause(deferredMedia);
      });

      if (activeDeferredMedia) play(activeDeferredMedia);
    };

    const queueSync = () => {
      if (syncQueued) return;
      syncQueued = true;
      requestAnimationFrame(syncPlayback);
    };

    const intersectionObserver = new IntersectionObserver(queueSync, {
      threshold: [0, 0.25, 0.5, 0.75, 1],
    });
    mediaItems.forEach((item) => intersectionObserver.observe(item));

    const mutationObserver = new MutationObserver(queueSync);
    mediaItems.forEach((item) => mutationObserver.observe(item, { attributes: true, attributeFilter: ['class'] }));

    viewer.addEventListener('slideChanged', queueSync);
    gallery.addEventListener('click', (event) => {
      if (event.target.closest('[data-target]')) window.setTimeout(queueSync, 550);
    });
    window.addEventListener('resize', queueSync, { passive: true });
    window.addEventListener('scroll', queueSync, { passive: true });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        activeDeferredMedia = null;
        deferredMediaElements.forEach(pause);
      } else {
        queueSync();
      }
    });

    queueSync();
  };

  const initializeWithin = (root = document) => {
    if (root.matches?.(gallerySelector)) initializeGallery(root);
    root.querySelectorAll?.(gallerySelector).forEach(initializeGallery);
  };

  initializeWithin();
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
